import config from 'config';
import moment from 'moment';

import axios from 'client';
import * as dto from 'client/dto';
import { CovidDataResponseModel } from 'models/CovidDataResponseModel';
import { parseIsoDateStartOfDayUtc0 } from 'utils/dateTimeHelpers';
import { triggerCsvDownload } from 'utils/downloadHelper';

import { LocationSearchResult } from 'components/_controls/AoiSearch/models';

import LocationApi from './LocationApi';

const apiVersion = '2020-06-30';

export type VaccineRiskCategories = {
  locationId: number;
  highRisk: dto.LocationVaccinationRateModel;
  totalAdult: dto.LocationVaccinationRateModel;
};

export type CovidDataLink = { url: string; expiry: string };

/**
 * Searches the supplied index for the top 5 facets of countries and top 10 for provinces.
 * Then queries the geoname endpoint for that information
 *
 * @param url the index url
 * @param searchText the text of location we're looking up
 * @returns
 */
function queryIndexByFacet(
  url: string,
  searchText: string,
  numCountries = 5,
  numProvinces = 10
): Promise<LocationSearchResult[]> {
  function queryFacets(
    locationType: dto.LocationType,
    count: number
  ): Promise<LocationSearchResult[]> {
    return axios
      .get(url, {
        withCredentials: false,
        headers: { 'api-key': config.locationIndexApiKey },
        params: {
          'api-version': apiVersion,
          search: `${searchText}*`,
          searchFields: 'displayName',
          $filter: `locationType eq ${locationType}`,
          facet: `locationId,count:${count}`,
          $top: 0, // We only care about the facets here, so take no value
          $orderby: 'locationId',
        },
      })
      .then(({ data }) => data['@search.facets']?.locationId?.map(({ value }) => value) ?? []);
  }

  const apiQueries: Promise<LocationSearchResult[]>[] = [];
  if (numCountries > 0) apiQueries.push(queryFacets(dto.LocationType.Country, numCountries));
  if (numProvinces > 0) apiQueries.push(queryFacets(dto.LocationType.Province, numProvinces));

  return Promise.all(apiQueries).then(res =>
    axios
      .post('/api/geoname', { geonameIds: [].concat(...res), includeShape: false })
      .then(({ data }) =>
        data.map(
          d =>
            ({
              id: d.geonameId,
              displayName: d.fullDisplayName,
              name: d.name,
              countryName: d.country,
              provinceName: d.province,
              locationType: d.locationType,
            } as LocationSearchResult)
        )
      )
  );
}

function getCovidDataByGeonameId({ locationId, skip = 0 }): Promise<CovidDataResponseModel[]> {
  return axios
    .get(`${config.searchIndexBaseUrl}${config.covidDSIndexUrl}`, {
      withCredentials: false,
      headers: { 'api-key': config.locationIndexApiKey },
      params: {
        'api-version': apiVersion,
        $top: 1000,
        $count: true,
        $select:
          'locationId,date,displayName,is_subnational,locationType,reported,deaths,weekly_avg_new_cases,weekly_avg_new_cases_per_100_000,cases_14_days,cases_14_days_per_100_000,cases_7_days,cases_7_days_per_100_000,total_tests_owid,tests_14_days,tests_14_days_per_100_000',
        $orderby: 'date asc',
        $filter: `locationId eq ${locationId}`,
        $skip: `${skip}`,
      },
    })
    .then(({ data }) =>
      data.value.map(
        d =>
          ({
            locationId: d.locationId,
            date: parseIsoDateStartOfDayUtc0(d.date),
            displayName: d.displayName,
            isSubnational: d.is_subnational,
            reported: d.reported,
            deaths: d.deaths,
            weeklyAvgNewCases: d.weekly_avg_new_cases,
            weeklyAvgNewCasesPer100K: d.weekly_avg_new_cases_per_100_000,
            casesPer7Days: d.cases_7_days,
            casesPer7DaysPer100K: d.cases_7_days_per_100_000,
            casesPer14Days: d.cases_14_days,
            casesPer14DaysPer100K: d.cases_14_days_per_100_000,
            totalTestsOwid: d.total_tests_owid,
            testsPer14Days: d.tests_14_days,
            testsPer14DaysPer100K: d.tests_14_days_per_100_000,
            locationType: d.locationType,
          } as CovidDataResponseModel)
      )
    );
}

/**
 * Search for the locations within the Vaccination index.
 * Queries the top 5 countries no matter the data and the top 10 province/states that have data in the result
 *
 * NOTE: Due to locations not having a unique table, we need to query for the facets
 * then get the location info using that
 * @param name the search name we're looking for
 */
function vaccineInfoLocationSearch(name: string): Promise<LocationSearchResult[]> {
  return Promise.all([
    LocationApi.searchLocations(name, 5, 0, 0, 0, 0),
    queryIndexByFacet(`${config.searchIndexBaseUrl}${config.vaccineIndexUrl}`, name, 0, 10),
  ]).then(res => [].concat(...res));
}

/**
 * Search for the locations within the Covid DS index.
 * Queries the top 5 countries and the top 10 province/states in the result
 *
 * NOTE: Due to locations not having a unique table, we need to query for the facets
 * then get the location info using that
 * @param name the search name we're looking for
 */
function covidDSLocationSearch(name: string): Promise<LocationSearchResult[]> {
  return queryIndexByFacet(`${config.searchIndexBaseUrl}${config.covidDSIndexUrl}`, name);
}

function getCovidDataLastUpdated(): Promise<Date> {
  return axios
    .get(`${config.searchIndexBaseUrl}${config.covidDSIndexUrl}`, {
      withCredentials: false,
      headers: { 'api-key': config.locationIndexApiKey },
      params: {
        'api-version': apiVersion,
        $top: 1,
        $orderby: 'date desc',
        $select: 'date',
      },
    })
    .then(({ data }) => parseIsoDateStartOfDayUtc0(data.value[0].date));
}

function downloadCovidData(): Promise<void> {
  return axios
    .get('/api/datasuite/covid-data/download', {
      responseType: 'blob',
    })
    .then(({ data }) =>
      triggerCsvDownload(
        data,
        `BlueDot COVID DS - Global Data ${moment(new Date()).format('YYYY-MM-DD')}.csv`,
        false
      )
    );
}

function getCovidDataDownloadLink(): Promise<CovidDataLink> {
  return axios.get('/api/datasuite/covid-data/download-link').then(({ data }) => data);
}

function getVaccinationInfo(
  model: dto.VaccinationDataRequestModel & {
    targetHighRiskRate: number;
    targetTotalRate: number;
  }
): Promise<VaccineRiskCategories[]> {
  function mapResponseWithFormattedDates(response: dto.LocationVaccinationRateModel) {
    if (!response) return response;

    return {
      ...response,
      targetVaccinationRateDate: response.targetVaccinationRateDate
        ? new Date(response.targetVaccinationRateDate)
        : null,
      targetVaccinationRateBestDate: response.targetVaccinationRateBestDate
        ? new Date(response.targetVaccinationRateBestDate)
        : null,
      targetVaccinationRateWorstDate: response.targetVaccinationRateWorstDate
        ? new Date(response.targetVaccinationRateWorstDate)
        : null,
      immuneUnvaccinatedLastUpdatedDate: response.immuneUnvaccinatedLastUpdatedDate
        ? new Date(response.immuneUnvaccinatedLastUpdatedDate)
        : null,
      historicalVaccinationData: response.historicalVaccinationData.map(histItem => ({
        ...histItem,
        date: new Date(histItem.date),
      })),
      predictedVaccinationData: response.predictedVaccinationData.map(predItem => ({
        ...predItem,
        date: new Date(predItem.date),
      })),
    } as dto.LocationVaccinationRateModel;
  }

  return Promise.all([
    axios.post<dto.LocationVaccinationRateModel[]>('/api/datasuite/vaccine', {
      startDate: model.startDate,
      endDate: model.endDate,
      locationIds: model.locationIds,
      category: dto.VaccinationDataCategory.HighRiskPopulation,
      targetRate: model.targetHighRiskRate,
    }),
    axios.post<dto.LocationVaccinationRateModel[]>('/api/datasuite/vaccine', {
      startDate: model.startDate,
      endDate: model.endDate,
      locationIds: model.locationIds,
      category: dto.VaccinationDataCategory.TotalAdultPopulation,
      targetRate: model.targetTotalRate,
    }),
  ]).then(([resHighRisk, resTotalPop]) =>
    model.locationIds.map(locationId => ({
      locationId,
      highRisk: mapResponseWithFormattedDates(
        (resHighRisk?.data ?? []).find(res => res.locationId === locationId)
      ),
      totalAdult: mapResponseWithFormattedDates(
        (resTotalPop?.data ?? []).find(res => res.locationId === locationId)
      ),
    }))
  );
}

export default {
  getCovidDataByGeonameId,
  covidDSLocationSearch,
  getCovidDataLastUpdated,
  downloadCovidData,
  getCovidDataDownloadLink,
  getVaccinationInfo,
  vaccineInfoLocationSearch,
};
